/**
 * Level of an access profile, from the rights it grants: the badge next to a profile, and what the
 * « Peut attribuer » rights unlock when a profile is given to someone.
 *   1 EMPLOYEE      self-service only (own leaves, expenses, trainings, the directory...)
 *   2 ADMIN         at least one right over other people's data or over a module's settings
 *   3 GLOBAL_ADMIN  access to the global settings (isSubAdminProfile), or to the wages
 *
 * Every path of the profile's `modules` is classified in PROFILE_RIGHT_LEVELS (a spec of the API
 * checks the schema against it, so a new right must be classified here). A right counts only when
 * its module is enabled (`isAccess`), and when it grants something: a true boolean, or an options
 * right whose value is mapped. `null` is not a right: a scope, zone ids or a toggle that only
 * narrows what the rights above it reach.
 */

export const PROFILE_LEVELS = { EMPLOYEE: 1, ADMIN: 2, GLOBAL_ADMIN: 3 } as const

export type TProfileLevel = 1 | 2 | 3

type TRightLevel = TProfileLevel | Partial<Record<string, TProfileLevel>> | null

const E = PROFILE_LEVELS.EMPLOYEE
const A = PROFILE_LEVELS.ADMIN
const G = PROFILE_LEVELS.GLOBAL_ADMIN
/** Options rights (ADMIN / WRITE / READ / LIMITED / NONE): writing for others is an admin right. */
const WRITE_IS_ADMIN = { WRITE: A, ADMIN: A }
const ADMIN_ONLY = { ADMIN: A }

const PROJECT_TABS = { editProject: A, timesheet: A, monitoring: A, budget: A, staffing: A, invoice: A, expenses: A }
const prefixed = (prefix: string, levels: Record<string, TRightLevel>) =>
  Object.fromEntries(Object.entries(levels).map(([key, level]) => [`${prefix}.${key}`, level]))

export const PROFILE_RIGHT_LEVELS: Record<string, Record<string, TRightLevel>> = {
  hr: {
    moduleId: null,
    isAccess: E,
    resourceProfileAccess: E,
    resourceOverview: null,
    resourceOverviewOfficeZoneIds: null,
    resourceOverviewCompanyZoneIds: null,
    hidePersonnalData: E,
    canSeeBirthdaysOnHome: E,
    canSeeBirthdayAgeOnHome: E,
    canSeeOthersProjects: E,
    accessSkill: ADMIN_ONLY,
    canEditOwnSkills: E,
    accessDocument: WRITE_IS_ADMIN,
    accessJobPath: WRITE_IS_ADMIN,
    accessTrainingOnProfile: E,
    accessOnboardingOnProfile: A,
    // Read by nothing any more (commented checks only): grants nothing
    tagSkills: null,
    canEditProfile: A,
    createResourceAccess: A,
    // Read by nothing: grants nothing
    canDeleteResource: null,
    canGrantAdminAccess: A,
    onboardingAccess: A,
    signatureCampaignAccess: A,
    alumniAccess: A,
    statsAccess: A,
    headcountAccess: A,
    headcountValidationAccess: A,
    // Wages are above admin: as sensitive as the global settings
    wageAccess: G,
    wageOverview: null,
    wageOverviewOfficeZoneIds: null,
    wageOverviewCompanyZoneIds: null,
    settingsAccess: A,
    settingsDepartureCategoryAccess: A,
    settingsDegreeFieldCategoryAccess: A,
    settingsEmployeeVariablesAccess: A,
    settingsStatusAccess: A,
    settingsContractSuperTypesAccess: A,
    settingsContractTypesAccess: A,
    settingsOnboardingProcessAccess: A,
    settingsMailTemplatesAccess: A,
    settingsFirmNewsAccess: A,
    settingsSignatureTemplatesAccess: A,
    settingsResourceCreationFieldsAccess: A,
  },
  recruitment: {
    moduleId: null,
    isAccess: E,
    myMeetingsAccess: E,
    recommendationAccess: E,
    jobBoardAccess: E,
    // Read by nothing: grants nothing
    personalStatsAccess: null,
    canDownloadInterviewCandidateFile: E,
    displayOnlyMyMeetingInCalendar: E,
    accessDocument: WRITE_IS_ADMIN,
    recruitmentOverview: null,
    recruitmentOverviewOfficeZoneIds: null,
    'processInterviews.isAccess': A,
    'processInterviews.seeCandidateMessages': A,
    'processInterviews.seeActionsOnProcess': A,
    'processInterviews.editCandidateInformations': A,
    screeningAccess: A,
    pipegAccess: A,
    candidateHistoryAccess: A,
    candidateBaseAccess: A,
    createCandidateAccess: A,
    jobBoardAdminAccess: A,
    statsAccess: A,
    recruitmentCalendarAccess: A,
    canGrantAdminAccess: A,
    canEditNotManaged: A,
    canDeleteInterview: A,
    canDecideInsteadOfRecruiterEvaluation: A,
    canReopenEvaluation: A,
    canReopenProcess: A,
    canDecideProcessResult: A,
    settingsAcess: A,
    settingsEvaluationsAccess: A,
    settingsWebsitesAccess: A,
    settingsMailTemplatesAccess: A,
    settingsOfficesAccess: A,
    settingsRejectionAccess: A,
    settingsWithdrawalAccess: A,
    settingsCustomQuestionsAccess: A,
    settingsHowDidYouFindUsAccess: A,
    settingsTagsAccess: A,
    settingsAcknowledgmentAccess: A,
  },
  leave: {
    moduleId: null,
    isAccess: E,
    calendarAccess: E,
    // Its route guard is no longer attached: grants nothing
    selfRequestAccess: null,
    newRequestAccess: E,
    canChooseValidator: E,
    canUpdateLeave: E,
    // Read by nothing: grants nothing
    canDeleteLeave: null,
    showAll: A,
    accountsAccess: A,
    requestAccess: A,
    bulkCreateAccess: A,
    editAllLeaves: A,
    canSelfValidate: A,
    leaveValidator: A,
    canCreateForOther: A,
    settingsAccess: A,
    settingsLeaveTypesAccess: A,
    settingsFormulasAccess: A,
    settingsPublicHolidaysAccess: A,
    settingsInitialBalanceAccess: A,
  },
  evaluation: {
    moduleId: null,
    isAccess: E,
    evaluationOverview: null,
    evaluationOverviewOfficeZoneIds: null,
    create360: A,
    createPeriod: A,
    createProject: A,
    createProjectSimplified: A,
    showAll: A,
    canEditEvaluation: A,
    accessCampaign: A,
    accessSummaries: A,
    canCreateSummary: A,
    canCreateCampaign: A,
    settingsAccess: A,
    settingsEvaluationFormsAccess: A,
    settingsEvaluationAveragesAccess: A,
    settingsEvaluationOfficesAccess: A,
  },
  project: {
    moduleId: null,
    isAccess: E,
    projectOverview: null,
    projectOverviewOfficeZoneIds: null,
    projectOverviewCompanyZoneIds: null,
    // Seeing every project, without a right on them, is no admin right: onGlobalAccess says what they allow
    overviewOnlyPublic: null,
    createAccess: A,
    projectAdvancementAccess: A,
    planningResourceAccess: WRITE_IS_ADMIN,
    canCreateClientProject: A,
    planningResourceScopeAll: A,
    statsAccess: A,
    settingsAccess: A,
    settingsProjectStatusAccess: A,
    settingsEngagementCategoryAccess: A,
    settingsCommissionCategoryAccess: A,
    settingsPricingCategoryAccess: A,
    settingsObjectItemCategoryAccess: A,
    ...prefixed('onGlobalAccess', PROJECT_TABS),
    ...prefixed('onDirectorProject', { isEnabled: null, ...PROJECT_TABS }),
    ...prefixed('onManagerProject', { isEnabled: null, ...PROJECT_TABS }),
    ...prefixed('onContributorProject', { isEnabled: null, ...PROJECT_TABS, timesheet: E, monitoring: E, expenses: E }),
  },
  timesheet: {
    moduleId: null,
    isAccess: E,
    showAll: A,
    settingsAccess: A,
    settingsActivitiesAccess: A,
    settingsDailyDetailsAccess: A,
  },
  training: {
    moduleId: null,
    isAccess: E,
    catalogAccess: E,
    myTrainingsAccess: E,
    elearningCatalogAccess: E,
    learningPathAccess: E,
    createTrainingAccess: A,
    trainingAccess: A,
    statisticsAccess: A,
    budgetAccess: A,
    wishesAttributionAccess: A,
    elearningBuilderAccess: A,
    elearningStatisticsAccess: A,
    elearningAssignmentAccess: A,
    canEditTraining: A,
    canEditElearning: A,
    canOrganizeSession: A,
    canReopenPresenceSheet: A,
    isAdmin: A,
    settingsAccess: A,
    settingsExternalTrainersAccess: A,
    settingsLocationsAccess: A,
    settingsCategoriesAccess: A,
    settingsObjectItemCategoryAccess: A,
    settingsCostCategoriesAccess: A,
    settingsEvaluationFormsAccess: A,
  },
  expense: {
    isAccess: E,
    canCreateExpense: E,
    canEditSettings: A,
    showAll: A,
  },
  ticket: {
    isAccess: E,
    ticketOverview: null,
    ticketOverviewOfficeZoneIds: null,
    showAll: A,
  },
  chat: {
    isAccess: E,
  },
  aiAssistant: {
    isAccess: E,
  },
  export: {
    moduleId: null,
    isAccess: E,
    canExportEvaluation: A,
    canExportProject: A,
    canExportResource: A,
    canExportCandidate: A,
    canExportTimeSheet: A,
  },
  salesFunnel: {
    moduleId: null,
    isAccess: E,
    showClients: E,
    showContacts: E,
    showActivities: E,
    askCrm: E,
    useAgents: E,
    viewRelationshipInsights: E,
    overviewOnlyPublic: null,
    projectOverview: null,
    projectOverviewOfficeZoneIds: null,
    projectOverviewCompanyZoneIds: null,
    showAll: A,
    showStats: A,
    editPipeline: A,
    viewInsightsAdvanced: A,
    manageWorkflows: A,
  },
  invoice: {
    moduleId: null,
    // The invoicing module itself is a finance tool: no self-service page
    isAccess: A,
    settingsAccess: A,
    settingsBankAccountsAccess: A,
    settingsInvoicingInformationAccess: A,
    settingsInvoicingTermsAccess: A,
    settingsMailTemplatesAccess: A,
    settingsExpenseCategoriesAccess: A,
    settingsEInvoicingAccess: A,
  },
  dashboard: {
    moduleId: null,
    isAccess: E,
    productionAccess: A,
    salesAccess: A,
    cashFlowAccess: A,
    salesTargetAccess: A,
    resourceAccess: A,
  },
  alert: {
    moduleId: null,
    isAccess: E,
    builderAccess: A,
    monitorAccess: A,
  },
  survey: {
    moduleId: null,
    isAccess: E,
    manageAccess: A,
    mySurveysAccess: E,
    settingsAccess: A,
  },
  // Enabled by isSubAdminProfile, not by an isAccess of its own: any access makes a global admin
  globalSettings: {
    userAccess: G,
    profileAccess: G,
    canGrantSuperAdminAccess: G,
    sessionAccess: G,
    personalisationAccess: G,
    consumptionAccess: G,
    openReplayAccess: G,
    integrationAccess: G,
    dataJobAccess: G,
    dataClientAccess: G,
    dataDegreeAccess: G,
    dataSkillAccess: G,
    dataOfficeAccess: G,
    dataDocumentTrackingAccess: G,
    dataDocumentStructureAccess: G,
    dataRoomAccess: G,
    dataResourceAccess: G,
    dataInvoicingInformationAccess: G,
    dataTeamAccess: G,
    dataUserGroupAccess: G,
  },
}

/** Nested toggles that switch a group of rights of their module on (project.onDirectorProject...). */
const GROUP_TOGGLES: Record<string, Record<string, (module: Record<string, any>) => boolean>> = {
  project: {
    onGlobalAccess: module => module.overviewOnlyPublic === false,
    onDirectorProject: module => module.onDirectorProject?.isEnabled === true,
    onManagerProject: module => module.onManagerProject?.isEnabled === true,
    onContributorProject: module => module.onContributorProject?.isEnabled === true,
  },
}

const valueAt = (source: Record<string, any> | undefined, path: string) =>
  path.split('.').reduce<any>((value, key) => value?.[key], source)

type TProfileRights = {
  isSubAdminProfile?: boolean | null
  modules?: Record<string, any> | null
}

/** Level of each right the profile grants, as { path: level }, the paths prefixed by their module. */
export const getProfileRightLevels = (profile: TProfileRights): Record<string, TProfileLevel> => {
  const granted: Record<string, TProfileLevel> = {}
  const modules = profile?.modules || {}

  Object.entries(PROFILE_RIGHT_LEVELS).forEach(([moduleKey, rights]) => {
    const module = modules[moduleKey]
    if (!module)
      return
    const isEnabled = moduleKey === 'globalSettings' ? profile.isSubAdminProfile === true : module.isAccess === true
    if (!isEnabled)
      return

    Object.entries(rights).forEach(([path, rightLevel]) => {
      if (rightLevel === null)
        return
      const toggle = GROUP_TOGGLES[moduleKey]?.[path.split('.')[0]]
      if (path.includes('.') && toggle && !toggle(module))
        return
      const value = valueAt(module, path)
      const level = typeof rightLevel === 'number'
        ? (value === true ? rightLevel : null)
        : (typeof value === 'string' ? rightLevel[value] ?? null : null)
      if (level)
        granted[`${moduleKey}.${path}`] = level
    })
  })

  return granted
}

/** The profile's level: its highest right, a global admin as soon as the global settings are enabled. */
export const computeProfileLevel = (profile: TProfileRights): TProfileLevel => {
  if (profile?.isSubAdminProfile === true)
    return G
  return Object.values(getProfileRightLevels(profile)).reduce<TProfileLevel>((level, right) => (right > level ? right : level), E)
}
